import { Component, type ReactNode } from "react";
import { AlertTriangle, RefreshCw, RotateCw, Trash2, Check } from "lucide-react";
import {
  canAttemptModuleImportRecovery,
  hasExceededModuleImportRecoveryAttempts,
  isModuleImportFailure,
  recoverFromModuleImportFailure,
  resetModuleImportRecoveryState,
} from "@/lib/moduleLoadRecovery";
import { PageLoader } from "@/components/PageLoader";
import { hideBootstrapLoader } from "@/lib/bootstrapLoader";

type Props = { children: ReactNode };

type ProgressStepId = "sw" | "caches" | "reload";
type ProgressStepStatus = "pending" | "running" | "done" | "skipped";
type ProgressStep = { id: ProgressStepId; label: string; status: ProgressStepStatus };

type State = {
  hasError: boolean;
  isModuleError: boolean;
  recoveryExhausted: boolean;
  isReloading: boolean;
  reloadMode: "hard" | "simple" | null;
  progress: ProgressStep[];
};

const INITIAL_PROGRESS: ProgressStep[] = [
  { id: "sw", label: "Removendo cache de aplicativo", status: "pending" },
  { id: "caches", label: "Limpando arquivos antigos", status: "pending" },
  { id: "reload", label: "Carregando versão atualizada", status: "pending" },
];

export class ModuleLoadBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    isModuleError: false,
    recoveryExhausted: false,
    isReloading: false,
    reloadMode: null,
    progress: INITIAL_PROGRESS,
  };

  static getDerivedStateFromError(error: unknown): State {
    const isModule = isModuleImportFailure(error);
    const recoveryExhausted = isModule
      ? !canAttemptModuleImportRecovery() || hasExceededModuleImportRecoveryAttempts()
      : false;
    return {
      hasError: true,
      isModuleError: isModule,
      recoveryExhausted,
      isReloading: false,
      reloadMode: null,
      progress: INITIAL_PROGRESS,
    };
  }

  override componentDidCatch(error: unknown) {
    if (isModuleImportFailure(error) && canAttemptModuleImportRecovery()) {
      void recoverFromModuleImportFailure().then((ok) => {
        if (!ok) this.setState({ recoveryExhausted: true });
      });
      return;
    }
    console.error("[ModuleLoadBoundary] unrecoverable load/render error:", error);
  }

  override componentDidUpdate(_prevProps: Props, prevState: State) {
    const shouldRevealFallback = this.state.hasError
      && (!this.state.isModuleError || this.state.recoveryExhausted)
      && (!prevState.hasError || (!prevState.recoveryExhausted && this.state.recoveryExhausted));

    if (shouldRevealFallback) {
      hideBootstrapLoader();
    }
  }

  private updateStep = (id: ProgressStepId, status: ProgressStepStatus) => {
    this.setState((prev) => ({
      progress: prev.progress.map((s) => (s.id === id ? { ...s, status } : s)),
    }));
  };

  /**
   * Hard reload that clears the service worker and HTTP caches before
   * reloading. Equivalent in effect to Ctrl/Cmd+Shift+R but reachable
   * with a single tap on mobile, where keyboard shortcuts don't exist.
   * Reports per-step progress for clearer UX feedback.
   */
  private handleHardReload = async () => {
    if (this.state.isReloading) return;
    this.setState({ isReloading: true, reloadMode: "hard", progress: INITIAL_PROGRESS });
    resetModuleImportRecoveryState();

    // Step 1: service worker
    this.updateStep("sw", "running");
    try {
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations
            .filter((r) => {
              const url = r.active?.scriptURL ?? r.installing?.scriptURL ?? "";
              return !url.includes("sw-push");
            })
            .map((r) => r.unregister()),
        );
        this.updateStep("sw", "done");
      } else {
        this.updateStep("sw", "skipped");
      }
    } catch {
      this.updateStep("sw", "done"); // best-effort
    }

    // Step 2: caches
    this.updateStep("caches", "running");
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
        this.updateStep("caches", "done");
      } else {
        this.updateStep("caches", "skipped");
      }
    } catch {
      this.updateStep("caches", "done");
    }

    // Step 3: reload
    this.updateStep("reload", "running");
    // Tiny delay so the user actually sees the final step transition.
    await new Promise((r) => setTimeout(r, 250));
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("v", Date.now().toString());
      window.location.replace(url.toString());
    } catch {
      window.location.reload();
    }
  };

  private handleSimpleRetry = () => {
    if (this.state.isReloading) return;
    this.setState({ isReloading: true, reloadMode: "simple" });
    resetModuleImportRecoveryState();
    window.location.reload();
  };

  private renderProgress() {
    return (
      <ol
        aria-live="polite"
        aria-busy={this.state.isReloading}
        className="mt-4 space-y-2 rounded-lg border border-border bg-muted/40 p-3"
      >
        {this.state.progress.map((step) => {
          const isDone = step.status === "done" || step.status === "skipped";
          const isRunning = step.status === "running";
          return (
            <li key={step.id} className="flex items-center gap-2 text-sm">
              <span
                className="flex h-5 w-5 shrink-0 items-center justify-center"
                aria-hidden="true"
              >
                {isRunning ? (
                  <RotateCw className="h-4 w-4 animate-spin text-primary" />
                ) : isDone ? (
                  <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                ) : (
                  <span className="h-2 w-2 rounded-full bg-muted-foreground/40" />
                )}
              </span>
              <span
                className={
                  isDone
                    ? "text-foreground"
                    : isRunning
                      ? "text-foreground"
                      : "text-muted-foreground"
                }
              >
                {step.label}
                {step.status === "skipped" && (
                  <span className="ml-1 text-xs text-muted-foreground">(não aplicável)</span>
                )}
              </span>
              <span className="sr-only">
                {isRunning
                  ? "em andamento"
                  : step.status === "done"
                    ? "concluído"
                    : step.status === "skipped"
                      ? "ignorado"
                      : "aguardando"}
              </span>
            </li>
          );
        })}
      </ol>
    );
  }

  override render() {
    if (!this.state.hasError) return this.props.children;

    // Module error still attempting auto-recovery — show spinner
    if (this.state.isModuleError && !this.state.recoveryExhausted) {
      return <PageLoader label="Restabelecendo módulos" fixed={false} />;
    }

    const isModule = this.state.isModuleError;
    const showProgress = this.state.isReloading && this.state.reloadMode === "hard";

    return (
      <div
        className="flex min-h-[100svh] items-center justify-center bg-background px-4 py-8 text-foreground"
        role="alert"
        aria-live="assertive"
      >
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-lg">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              <AlertTriangle className="h-5 w-5" aria-hidden="true" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold leading-tight">
                {isModule ? "Atualização disponível" : "Erro inesperado"}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {isModule ? (
                  <>
                    Detectamos que o seu navegador está usando uma versão antiga em
                    cache do aplicativo, e ela não é mais compatível com a versão
                    publicada.
                    <br />
                    <span className="mt-2 block">
                      Toque em <strong>Limpar cache e atualizar</strong> para baixar a
                      versão mais recente. Seus dados estão seguros — nada será perdido.
                    </span>
                  </>
                ) : (
                  "Ocorreu um erro ao carregar esta tela. Tente recarregar a página."
                )}
              </p>
            </div>
          </div>

          {showProgress && this.renderProgress()}

          <div className="mt-6 flex flex-col gap-2">
            <button
              type="button"
              onClick={this.handleHardReload}
              disabled={this.state.isReloading}
              aria-busy={this.state.isReloading && this.state.reloadMode === "hard"}
              aria-describedby="hard-reload-hint"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60"
            >
              {this.state.isReloading && this.state.reloadMode === "hard" ? (
                <>
                  <RotateCw className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Atualizando…
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                  Limpar cache e atualizar
                </>
              )}
            </button>
            <p id="hard-reload-hint" className="sr-only">
              Remove o cache do aplicativo e baixa a versão mais recente do servidor.
            </p>

            <button
              type="button"
              onClick={this.handleSimpleRetry}
              disabled={this.state.isReloading}
              aria-busy={this.state.isReloading && this.state.reloadMode === "simple"}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-transparent px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60"
            >
              {this.state.isReloading && this.state.reloadMode === "simple" ? (
                <>
                  <RotateCw className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Recarregando…
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                  Apenas recarregar
                </>
              )}
            </button>
          </div>

          {isModule && (
            <p className="mt-4 text-xs text-muted-foreground">
              Dica: se o problema persistir, no computador use{" "}
              <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                Ctrl
              </kbd>{" "}
              +{" "}
              <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                Shift
              </kbd>{" "}
              +{" "}
              <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                R
              </kbd>{" "}
              (ou ⌘+Shift+R no Mac).
            </p>
          )}
        </div>
      </div>
    );
  }
}
